/**
 * Profile utilities - migrated to use backend API
 */

import { api } from "@/lib/api";

export interface UserProfile {
  id: string;
  clerk_user_id?: string;
  email: string;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  image_url?: string | null;
  banner_url?: string | null;
  bio?: string | null;
  is_admin?: boolean | null;
  is_mentor?: boolean | null;
  location?: string | null;
  languages?: string[] | null;
  twitter_url?: string | null;
  linkedin_url?: string | null;
  github_url?: string | null;
  website_url?: string | null;
  superwhere_x_username?: string | null;
  youtube_channel_handle?: string | null;
  newsletter_subscribed?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Get user profile from backend API (by user ID)
 */
export async function getUserProfile(userId: string, email?: string): Promise<UserProfile | null> {
  try {
    const data = await api<UserProfile>(`/users/${userId}`, { cache: 'no-store' });
    return data || null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
}

/**
 * Get current user's full profile (auth required). More reliable for "my" profile.
 */
export async function getMyProfile(): Promise<UserProfile | null> {
  try {
    const data = await api<UserProfile>('/users/me', { cache: 'no-store' });
    return data || null;
  } catch (error) {
    console.error('Error fetching my profile:', error);
    return null;
  }
}

/**
 * Lookup user by username (for public mentor stream URLs)
 */
export async function getUserByUsername(username: string): Promise<UserProfile | null> {
  try {
    const data = await api<UserProfile>(`/users/by-username/${encodeURIComponent(username)}`);
    return data || null;
  } catch (error) {
    console.error('Error fetching user by username:', error);
    return null;
  }
}

/**
 * Update user profile via backend API
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'id' | 'clerk_user_id' | 'created_at'>>,
  email?: string
): Promise<UserProfile | null> {
  try {
    const response = await api<{ success: boolean; profile?: UserProfile }>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    // Return the updated profile directly if available
    return response.profile || null;
  } catch (error) {
    console.error('Error updating user profile:', error);
    return null;
  }
}
